import clsx from 'clsx'

import { useMemo, useEffect, useRef } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/athos/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { ResultImage } from 'libs/athos/components/ResultImage'
import { Price } from 'libs/athos/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/athos/components/Badge'
import { Promoline } from 'libs/athos/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/athos/hooks/useSwatches'
import { isVariantOutOfStock, getVariantSizeLabel } from 'libs/athos/hooks/useQuickAdd'
import { ResultsSwatches } from 'libs/athos/components/ResultSwatches'
import { ResultQuickAdd } from 'libs/athos/components/ResultQuickAdd'
import { CompactResultCard } from 'libs/athos/components/CompactResultCard'
import { WishlistCardButton } from 'libs/swym/components/WishlistCardButton'
import { WishlistSizeSelector } from 'libs/swym/components/WishlistSizeSelector'
import { swymService } from 'libs/swym/swym-service'
import { SIZE_SELECTED_CPROPS, PERSIST_DEBOUNCE_MS, isSizeSelected } from 'libs/swym/swym-entry'

const DEFAULT_OPTIONS = {
  layout: 'full',
  hideQuickAdd: false,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false,
  hideWishlist: false,
  showQuickAddDefault: false,
  showSizeSelector: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0', quickAddStyle = 'both', peek = false, insideCarousel = false, priority = false, imageSizes, savedVariantId = null, wishlistEntry = null, onPersistEntry = null }) {
  // Compact is a distinct horizontal card for narrow contexts (mini cart).
  // Full is the default and remains unchanged below.
  if (options?.layout === 'compact') {
    return <CompactResultCard result={result} dataIndex={dataIndex} />
  }

  const [variant] = result.variants
  const isB2BCustomer = window.globals?.customer?.b2b

  // For non-B2B customers, use regular pricing
  const finalPrice = variant.price
  const finalCompareAtPrice = variant.compare_at_price

  const {
    hideQuickAdd = DEFAULT_OPTIONS.hideQuickAdd,
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges,
    hideWishlist = DEFAULT_OPTIONS.hideWishlist,
    showQuickAddDefault = DEFAULT_OPTIONS.showQuickAddDefault,
    showSizeSelector = DEFAULT_OPTIONS.showSizeSelector
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  // Turns a swatch swap into a Swym write, but only when the card is
  // entry-scoped (`wishlistEntry`/`onPersistEntry` are supplied only by
  // WishlistPage), so PLP/search/recs swatch clicks never reach it. The check
  // is "does the rendered product still match the saved entry?", not "was a
  // swatch clicked?".
  const swapPersistTimer = useRef(null)
  useEffect(() => {
    if (!wishlistEntry || !onPersistEntry) return
    if (Number(currentResult.parentId) === Number(wishlistEntry.empi)) return

    clearTimeout(swapPersistTimer.current)
    swapPersistTimer.current = setTimeout(async () => {
      // Carry the saved size across only if the sibling has that same size
      // label in stock; otherwise fall back to a product-level save so the
      // card renders "Select Size" rather than silently keeping a size the
      // shopper never chose for this colour.
      const savedSizeVariant = savedVariantId
        ? result.variants?.find((v) => Number(v.id) === Number(savedVariantId))
        : null
      const savedSizeLabel = savedSizeVariant ? getVariantSizeLabel(savedSizeVariant) : null
      const carriedVariant = savedSizeLabel
        ? currentResult.variants?.find((v) => getVariantSizeLabel(v) === savedSizeLabel && !isVariantOutOfStock(v))
        : null
      const siblingEmpi = Number(currentResult.parentId ?? currentResult.uid)
      // If the sibling already has a cprops-less entry of its own, target its
      // exact variant rather than recomputing "first available" — stock can
      // shift between the two saves, and a different variant would create a
      // second product-level entry (a duplicate "Select Size" card).
      const existingProductLevelEntry = swymService.entriesFor(siblingEmpi).find((e) => !isSizeSelected(e))
      const existingProductLevelVariant = existingProductLevelEntry
        ? currentResult.variants?.find((v) => Number(v.id) === Number(existingProductLevelEntry.epi))
        : null
      const targetVariant =
        carriedVariant ??
        existingProductLevelVariant ??
        currentResult.variants?.find((v) => !isVariantOutOfStock(v)) ??
        currentResult.variants?.[0]
      if (!targetVariant) return // sibling has no variants at all — nothing to save

      const newEntry = {
        epi: Number(targetVariant.id),
        empi: siblingEmpi,
        du: `${window.location.origin}/products/${currentResult.handle}`,
        ...(carriedVariant ? { cprops: SIZE_SELECTED_CPROPS } : {})
      }
      const ok = await onPersistEntry(wishlistEntry, newEntry)
      if (!ok) {
        console.warn('[wishlist] swatch change failed to save, reverting to the saved colour')
        updateSwatch(result.handle)
      }
    }, PERSIST_DEBOUNCE_MS)

    return () => clearTimeout(swapPersistTimer.current)
  }, [currentResult, wishlistEntry, onPersistEntry, savedVariantId, result, updateSwatch])

  const criteria = useMemo(
    () => ({
      tags: currentResult?.tags ?? [],
      collections: currentResult?.collection_handle ?? [],
      products: [currentResult.handle],
      location: 'PLP'
    }),
    [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle]
  )

  const isOutlet = (criteria.tags?.includes('IsOutlet:true') || criteria.tags?.includes('x-markdown:yes')) ?? false
  // Metaobject variables
  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)

  // Generic function to get first two items with exclusive logic
  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  // Colourway messaging: "X Colours Available" for multi-colour styles,
  // "In {Colour Name}" for a single colourway. Swatches replace it on hover.
  const swatchCount = result.swatches?.length ?? 0
  // Mobile card: swatches only earn their slot when there is more than one colourway.
  // A single-colourway product shows its review stars in that slot instead, so the two
  // are mutually exclusive below `lg`. Desktop keeps both (colour messaging + stars).
  const showSwatchesOnMobile = swatchCount > 1
  // Surfaces can drop swatches altogether (the `hide_swatches` carousel setting), in which
  // case the stars keep the mobile slot no matter how many colourways the product has.
  const swatchesOwnMobileSlot = !hideSwatches && showSwatchesOnMobile
  const colourMessage = useMemo(() => {
    if (swatchCount > 1) return `${swatchCount} Colours Available`
    const currentSwatch = result.swatches?.find((swatch) => swatch.handle === currentResult.handle) ?? result.swatches?.[0]
    if (!currentSwatch?.colourName) return null
    const colourName = currentSwatch.colourName.toLowerCase().replace(/^\w/, (char) => char.toUpperCase())
    return `${colourName}`
  }, [result.swatches, swatchCount, currentResult.handle])

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  return (
    <div
      className={clsx('group/card relative flex', className)}
      id={`result__${currentResult.handle}`}
      data-dl-track="select_item"
      data-dl-product-id={currentResult.parentId}
      data-dl-index={dataIndex}
    >
      <div className="flex w-full grow flex-col gap-y-0.625">
        <div className="relative w-full lg:overflow-hidden">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            {/* Peek is structurally impossible inside a carousel, whatever a caller passes. */}
            <ResultImage result={currentResult} peek={peek && !insideCarousel} insideCarousel={insideCarousel} priority={priority} {...(imageSizes && { sizes: imageSizes })}>
              {!hideBadges && (firstBadge || secondBadge) && (
                <div className="absolute left-0 top-0 flex z-20 max-w-[80%] flex-col gap-y-0.125 p-[6px] lg:p-1 justify-center items-start">
                  {firstBadge && <Badge badge={firstBadge} badgeLocation="PLP" product={currentResult} />}
                  {/* Mobile shows one badge per card. `lg:contents` hands the badge itself
                      back to this flex column at desktop, so `gap-y-0.125` still applies. */}
                  {secondBadge && (
                    <div className="hidden lg:contents">
                      <Badge badge={secondBadge} badgeLocation="PLP" product={currentResult} />
                    </div>
                  )}
                </div>
              )}
            </ResultImage>
          </a>
          {/* Quick Add */}
              {!hideQuickAdd && currentResult.is_giftcard !== 'true' && (
                <ResultQuickAdd device={quickAddStyle} result={currentResult} showByDefault={showQuickAddDefault} className="absolute inset-x-0 bottom-0 top-0" />
              )}
          {/* Wishlist */}
          {!hideWishlist && window.globals?.enableWishlist && (
            <WishlistCardButton result={currentResult} entry={wishlistEntry} />
          )}
        </div>
          <div className="flex flex-col gap-y-0.25 px-1 py-0.5 lg:py-1">
            {/* Product name + price row — price sits below the bottom right of the image */}
            <div className="flex flex-col lg:flex-row items-start lg:justify-between gap-x-1">
              <a
                href={url}
                onClick={handleLogClick}
                className="paragraph-xs lg:paragraph-s font-medium link-fade min-w-0 text-pretty"
                aria-label={currentResult.name}
                dangerouslySetInnerHTML={{ __html: currentResult.name }}
              />
              {isB2BCustomer ? (
                <b2b-price className="group/price flex shrink-0 gap-x-0.25" data-variant-id={result.variants[0].id}>
                  <span
                    data-original-price
                    className="paragraph-xs lg:paragraph-s hidden font-medium line-through group-data-[sale=true]/price:block"
                  ></span>
                  <span
                    data-b2b-price
                    className="paragraph-xs lg:paragraph-s font-medium group-data-[sale=true]/price:text-brand-sale"
                  ></span>
                </b2b-price>
              ) : (
                <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} className="flex shrink-0 gap-x-0.25" isOutlet={isOutlet} />
              )}
            </div>

                {/* Promoline sits above the swatch/review slot on mobile */}
                {!hidePromolines && firstPromoline && (
                <div className="justify-start flex lg:hidden my-0.5">
                  <Promoline promoline={firstPromoline} promolineLocation="PLP" />
                </div>
                )}

            <div className="flex justify-start lg:justify-between gap-x-1 flex-col lg:flex-row">
              {/* `lg-max:min-h-1.5` reserves the slot's height on mobile whichever branch
                  renders (swatches, stars, or neither) so grid rows stay even. */}
              <div className="flex min-w-0 w-full flex-col items-start gap-y-0.25 lg:max-w-[50%] swatch-wrapper">
                {/* Colourway messaging (desktop default) / swatches (mobile + desktop hover) */}
                {!hideSwatches && (
                  <div className={clsx('flex h-1.5 items-center w-full', !showSwatchesOnMobile && 'lg-max:hidden')}>
                    {colourMessage && (
                      <p className="paragraph-xs text-type-black-tertiary hidden lg:block lg:group-hover/card:hidden  flex-shrink-0 flex-nowrap whitespace-nowrap">{colourMessage}</p>
                    )}
                    <div className={clsx('lg:group-hover/card:flex items-center flex-shrink-0 flex-nowrap whitespace-nowrap', showSwatchesOnMobile ? 'flex lg:hidden' : 'hidden')}>
                      <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />
                    </div>
                  </div>
                )}

                {/* Review stars/count sit under the colour messaging on desktop. On mobile
                    they take the slot the swatches would have used, so they are hidden when
                    swatches are showing. Klaviyo renders nothing for a product with no
                    reviews, so "only if it has reviews" needs no handling here. Kept as one
                    element rather than two conditional copies — a duplicate would double
                    Klaviyo's `renderWidgets()` DOM scan for every card. */}
                <klaviyo-star-rating
                  className={clsx(swatchesOwnMobileSlot && 'lg-max:hidden')}
                  product-id={currentResult.parentId}
                  product-title={currentResult.name}
                  product-type={currentResult.type}
                ></klaviyo-star-rating>
              </div>

              {/* Desktop promoline sits opposite the colour messaging */}
              {!hidePromolines && firstPromoline && (
                <div className="justify-start hidden lg:flex lg:justify-end lg:text-right">
                  <Promoline promoline={firstPromoline} promolineLocation="PLP" />
                </div>
              )}
            </div>
            {showSizeSelector && (
              <WishlistSizeSelector
                result={currentResult}
                savedVariantId={savedVariantId}
                entry={wishlistEntry}
                onPersistEntry={onPersistEntry}
                className="pt-1"
              />
            )}
          </div>
      </div>
    </div>
  )
}
